import type * as lazyIndex from './lazyIndex';
import {
    LazyImport,
    LazyModule,
    LazyAction,
    createLazyComponent,
    registerLazyOrchestrator,
    type LazyComponent,
} from 'owa-bundling';
import { getSuggestions } from 'owa-search-actions/lib/actions/getSuggestions';
import { searchSessionStarted } from 'owa-search-actions/lib/actions/searchSessionStarted';
import { startSearch } from 'owa-search-actions/lib/actions/startSearch';
import { onListViewTypeOptionSaved } from 'owa-options-actions/lib/onListViewTypeOptionSaved';
import getSelectedSearchScopeDisplayName from './utils/getSelectedSearchScopeDisplayName';
import type { FolderScopePickerProps } from './components/FolderScopePicker';
import type { SearchHeaderProps } from './components/SearchHeader';
import type { SearchHeaderFirstRowContentProps } from './components/SearchHeaderFirstRowContent';

export const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(
    () => import(/* webpackChunkName: "MailSearch"*/ './lazyIndex'),
    { name: 'MailSearch' }
);
export const SearchHeaderFirstRowContent: LazyComponent<
    SearchHeaderFirstRowContentProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.SearchHeaderFirstRowContent);

export const LazySearchHeader: LazyComponent<SearchHeaderProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.SearchHeader);

// Export lazy loaded components
export const FolderScopePicker: LazyComponent<FolderScopePickerProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.FolderScopePicker, undefined, undefined, {
        onlyImportIf: () => !!getSelectedSearchScopeDisplayName(),
    });
export const lazySetStaticSearchScopeData: LazyImport<
    typeof lazyIndex.setStaticSearchScopeData,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.setStaticSearchScopeData);

export const lazyStartSearchWithCategory: LazyImport<
    typeof lazyIndex.startSearchWithCategory,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.startSearchWithCategory);
export const lazyFindEmailFromSender: LazyAction<
    typeof lazyIndex.findEmailFromSender,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.findEmailFromSender);

export const lazyFindEmailFromConversation: LazyAction<
    typeof lazyIndex.findEmailFromConversation,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.findEmailFromConversation);

export const lazyPersonaControlStore: LazyImport<
    typeof lazyIndex.personaControlStore,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.personaControlStore);
export const lazyCreateFallbackPersonaSearchTableQuery: LazyImport<
    typeof lazyIndex.createFallbackPersonaSearchTableQuery,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.createFallbackPersonaSearchTableQuery);

export const lazyCreateFallbackCategorySearchTableQuery: LazyImport<
    typeof lazyIndex.createFallbackCategorySearchTableQuery,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.createFallbackCategorySearchTableQuery);

export const lazyInitializeSearch: LazyAction<
    typeof lazyIndex.initializeSearch,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.initializeSearch);

// Register lazy orchestrator
registerLazyOrchestrator(getSuggestions, lazyModule, m => m.getSuggestionsOrchestrator);
registerLazyOrchestrator(searchSessionStarted, lazyModule, m => m.searchSessionStartedOrchestrator);
registerLazyOrchestrator(startSearch, lazyModule, m => m.startSearchOrchestrator, {
    captureBundleTime: true,
});
registerLazyOrchestrator(startSearch, lazyModule, m => m.startGqlSearchOrchestrator);
registerLazyOrchestrator(startSearch, lazyModule, m => m.startAllMailboxSearchOrchestrator);
registerLazyOrchestrator(
    onListViewTypeOptionSaved,
    lazyModule,
    m => m.onSearchListViewTypeOptionSavedOrchestrator
);
